import { DoubleState, useDoubleStore } from '@/context/DoubleProvider'
import { useSession } from '@/context/SessionProvider'
import { Box, Flex, SimpleGrid, Text, useMediaQuery } from '@chakra-ui/react'
import { memo, useEffect, useRef, useState } from 'react'
import { getDoubleWinningColor } from '@/helpers/getDoubleWinningColor'
import { DoubleField } from '../atoms/DoubleField'
import { AnimatedAmount } from '../../../atoms/AnimatedAmount/AnimatedAmount'
import { BigNumberInWei } from '@injectivelabs/utils'
import { TokenMoney } from '@ninjablaze/sdk-ts'

import { DoubleColor } from '@/__generated__/graphql'
import { useShallow } from 'zustand/react/shallow'
import { useStoreWithEqualityFn } from 'zustand/traditional'

export const MobileDoubleBets = memo(({ colors }: { colors: Array<DoubleColor> }) => {
  const bets = useStoreWithEqualityFn(
    useDoubleStore,
    (state) => state.bets,
    (oldBets, newBets) => JSON.stringify(oldBets) === JSON.stringify(newBets)
  )
  const denom = useDoubleStore((state) => state.denom)
  const state = useDoubleStore((state) => state.state)
  const isRoundFinished = useDoubleStore((state) => state.isRoundFinished)

  const currentRoll = useDoubleStore(
    useShallow((state) => {
      if (state.isRoundFinished) {
        return state.currentRoll
      }

      return null
    })
  )
  const { account } = useSession()
  const isRendered = useRef(false)
  const [isDisabled, setDisabled] = useState(false)
  const [winningColor, setWinningColor] = useState(getDoubleWinningColor(currentRoll))
  const [isDesktop] = useMediaQuery('(min-width: 768px)')

  useEffect(() => {
    if (isRendered.current) {
      setDisabled(isRoundFinished && state === DoubleState.FINISHED)
    }

    setWinningColor(getDoubleWinningColor(currentRoll))
    isRendered.current = true
  }, [isRoundFinished])

  if (isDesktop) {
    return null
  }

  return (
    <Box bg={'ninja.600'} rounded={'3xl'} p={3} pb={4} mb={{ base: 9, md: 0 }}>
      <Text color={'ninja.100'} fontWeight={700} mb={3} textAlign={'center'}>
        Your bets
      </Text>
      <SimpleGrid columns={3}>
        {colors.map((color) => {
          const playerBet = account
            ? new TokenMoney(
                bets[color]
                  .filter((bet) => bet.player === account?.injectiveAddress && bet.denom.replaceAll('_', '/') === denom)
                  .reduce((prev, curr) => prev.plus(curr.bet), new BigNumberInWei(0))
                  .toString(),
                denom
              ).humanFormat
            : null
          const isWinningColor = winningColor === color
          const isHighlighted = isWinningColor && isDisabled

          return (
            <Flex key={color} gap={2.5} justifyContent={'center'} alignItems={'center'} flexDir={'column'}>
              <DoubleField isHighlighted={isHighlighted} isDisabled={isDisabled} boxSize={12} type={color} />
              <AnimatedAmount
                minWidth="30px"
                amountProps={{
                  currencyStyles: { display: 'none' },
                  wrapperStyles: {
                    fontSize: 'sm',
                    color: isDisabled ? (isWinningColor ? 'brand.green' : 'brand.error') : 'white',
                  },
                  denom: denom,
                }}
                value={playerBet ? parseFloat(playerBet) : 0}
              />
            </Flex>
          )
        })}
      </SimpleGrid>
    </Box>
  )
})
